import React, { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { useBranch } from "@/context/BranchContext";
import { rupiah, dateTimeFmt, todayISO } from "@/lib/format";
import { downloadCSV } from "@/lib/export";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  HandHeart, Plus, Trash2, Loader2, Save, Download, FileText, AlertTriangle,
} from "lucide-react";

export default function SpecialSales() {
  const { branches } = useBranch();
  const branchOptions = useMemo(
    () => branches.filter((b) => b.active !== false),
    [branches]
  );

  // --- form state ---
  const [branchId, setBranchId] = useState("");
  const [mainBranch, setMainBranch] = useState({ id: null, name: null });
  const [products, setProducts] = useState([]);
  const [loadingProds, setLoadingProds] = useState(false);
  const [cart, setCart] = useState([]); // {product_id, name, unit, qty, units:[...]}
  const [pick, setPick] = useState(""); // selected product id to add
  const [recipient, setRecipient] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // --- list/report state ---
  const [start, setStart] = useState(todayISO().slice(0, 8) + "01");
  const [end, setEnd] = useState(todayISO());
  const [filterBranch, setFilterBranch] = useState("all");
  const [recipientFilter, setRecipientFilter] = useState("");
  const [report, setReport] = useState({ count: 0, total_loss: 0, items: [] });
  const [loadingList, setLoadingList] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    client.get("/special-sales/main-branch")
      .then((r) => {
        setMainBranch(r.data);
        if (r.data?.id) setBranchId(r.data.id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const loadProducts = (bid) => {
    if (!bid) return;
    setLoadingProds(true);
    client.get("/products", { params: { branch_id: bid } })
      .then((r) => setProducts(r.data.filter((p) => !p.ready_to_eat)))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoadingProds(false));
  };

  useEffect(() => { loadProducts(branchId); setCart([]); /* eslint-disable-next-line */ }, [branchId]);

  const loadList = () => {
    setLoadingList(true);
    const params = { start, end };
    if (filterBranch !== "all") params.branch_id = filterBranch;
    if (recipientFilter.trim()) params.recipient = recipientFilter.trim();
    client.get("/special-sales", { params })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoadingList(false));
  };

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);

  const addToCart = () => {
    const p = products.find((x) => x.id === pick);
    if (!p) { toast.error("Pilih produk dulu"); return; }
    if (cart.some((c) => c.product_id === p.id)) { toast.info("Produk sudah ada di daftar"); return; }
    setCart((s) => [...s, {
      product_id: p.id, name: p.name, unit: p.base_unit,
      qty: 1, units: p.units, stock_qty: p.stock_qty, avg_cost: p.avg_cost || 0,
    }]);
    setPick("");
  };

  const updateLine = (pid, patch) =>
    setCart((s) => s.map((c) => (c.product_id === pid ? { ...c, ...patch } : c)));
  const removeLine = (pid) => setCart((s) => s.filter((c) => c.product_id !== pid));

  const submit = async () => {
    if (!branchId) { toast.error("Gudang utama belum siap, mohon tunggu"); return; }
    if (!cart.length) { toast.error("Tambahkan minimal satu produk"); return; }
    if (!recipient.trim()) { toast.error("Nama penerima wajib diisi"); return; }
    if (!note.trim()) { toast.error("Keterangan wajib diisi"); return; }
    setSaving(true);
    try {
      await client.post("/special-sales", {
        branch_id: branchId,
        recipient_name: recipient.trim(),
        note: note.trim(),
        items: cart.map((c) => ({ product_id: c.product_id, unit: c.unit, qty: Number(c.qty) })),
      });
      toast.success("Penjualan khusus tersimpan, stok dipotong");
      setCart([]); setRecipient(""); setNote("");
      loadProducts(branchId);
      loadList();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const exportCSV = () => downloadCSV(
    `penjualan-khusus-${start}_${end}`,
    ["No", "Tanggal", "Cabang", "Penerima", "Keterangan", "Jumlah Item", "Nilai HPP (Kerugian)", "Dibuat Oleh"],
    report.items.map((d) => [
      d.number, dateTimeFmt(d.created_at), d.branch_name, d.recipient_name, d.note,
      (d.items || []).length, Math.round(d.cogs_total || 0), d.created_by_name,
    ])
  );

  return (
    <div className="p-6 space-y-6 animate-in-up" data-testid="special-sales-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
          <HandHeart className="h-7 w-7 text-rose-600" /> Penjualan Khusus
        </h1>
        <p className="text-sm text-muted-foreground">
          Penyerahan produk tanpa penerimaan uang. Stok dipotong, tidak menambah omzet, nilai HPP menjadi kerugian yang mengurangi laba bersih.
        </p>
      </div>

      {/* ---------- FORM ---------- */}
      <Card className="border-rose-200">
        <CardHeader>
          <CardTitle className="text-lg">Buat Transaksi Baru</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Gudang Utama</Label>
              <div
                className="flex h-10 items-center rounded-md border border-input bg-muted px-3 text-sm font-medium"
                data-testid="special-branch-locked"
              >
                {mainBranch.name || "Memuat gudang utama…"}
              </div>
              <p className="text-xs text-muted-foreground">
                Penjualan khusus hanya diambil dari gudang utama region Anda.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Nama Penerima <span className="text-rose-600">*</span></Label>
              <Input value={recipient} onChange={(e) => setRecipient(e.target.value)}
                placeholder="Nama penerima barang" data-testid="special-recipient-input" />
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[220px] space-y-1.5">
              <Label>Tambah Produk</Label>
              <Select value={pick} onValueChange={setPick} disabled={loadingProds}>
                <SelectTrigger data-testid="special-product-select">
                  <SelectValue placeholder={loadingProds ? "Memuat produk…" : "Pilih produk"} />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} · stok {p.stock_qty} {p.base_unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" variant="outline" onClick={addToCart} data-testid="special-add-item-btn">
              <Plus className="mr-1 h-4 w-4" /> Tambah
            </Button>
          </div>

          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produk</TableHead>
                  <TableHead className="w-32">Satuan</TableHead>
                  <TableHead className="w-28 text-right">Qty</TableHead>
                  <TableHead className="w-40 text-right">Est. HPP (Kerugian)</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cart.map((c) => {
                  const u = c.units.find((x) => x.name === c.unit);
                  const estHpp = (Number(c.qty) || 0) * (u ? u.factor : 1) * (c.avg_cost || 0);
                  return (
                    <TableRow key={c.product_id} data-testid={`special-cart-row-${c.product_id}`}>
                      <TableCell className="font-medium">{c.name}
                        <div className="text-xs text-muted-foreground">stok {c.stock_qty}</div>
                      </TableCell>
                      <TableCell>
                        <Select value={c.unit} onValueChange={(v) => updateLine(c.product_id, { unit: v })}>
                          <SelectTrigger className="h-9" data-testid={`special-cart-unit-${c.product_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {c.units.map((un) => <SelectItem key={un.name} value={un.name}>{un.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" min="0" value={c.qty}
                          onChange={(e) => updateLine(c.product_id, { qty: e.target.value })}
                          className="h-9 w-24 text-right" data-testid={`special-cart-qty-${c.product_id}`} />
                      </TableCell>
                      <TableCell className="text-right font-mono text-rose-600" data-testid={`special-cart-hpp-${c.product_id}`}>
                        {rupiah(estHpp)}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => removeLine(c.product_id)}
                          data-testid={`special-cart-remove-${c.product_id}`}>
                          <Trash2 className="h-4 w-4 text-rose-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!cart.length && <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">Belum ada produk ditambahkan</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>

          <div className="space-y-1.5">
            <Label>Keterangan <span className="text-rose-600">*</span></Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder="Alasan / tujuan penyerahan (wajib)" data-testid="special-note-input" />
          </div>

          <div className="flex items-center justify-between rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Transaksi ini tidak menambah omzet. Nilai HPP akan dihitung sebagai kerugian.</span>
            <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="special-submit-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Simpan & Potong Stok
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ---------- REPORT ---------- */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Daftar & Laporan Penjualan Khusus</CardTitle>
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={!report.items.length} data-testid="special-export-btn">
            <Download className="mr-1 h-4 w-4" /> Excel/CSV
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Dari</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="special-filter-start" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Sampai</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="special-filter-end" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Cabang</Label>
              <Select value={filterBranch} onValueChange={setFilterBranch}>
                <SelectTrigger className="w-48" data-testid="special-filter-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Cabang</SelectItem>
                  {branchOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Penerima</Label>
              <Input value={recipientFilter} onChange={(e) => setRecipientFilter(e.target.value)} placeholder="Cari penerima" className="w-44" data-testid="special-filter-recipient" />
            </div>
            <Button onClick={loadList} data-testid="special-filter-apply">Terapkan</Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border bg-white p-4">
              <div className="text-xs text-muted-foreground">Jumlah Transaksi</div>
              <div className="text-2xl font-bold" data-testid="special-total-count">{report.count}</div>
            </div>
            <div className="rounded-lg border bg-rose-50 p-4">
              <div className="text-xs text-rose-700">Total Kerugian (Nilai HPP)</div>
              <div className="text-2xl font-bold text-rose-700" data-testid="special-total-loss">{rupiah(report.total_loss)}</div>
            </div>
          </div>

          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>No</TableHead>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Cabang</TableHead>
                  <TableHead>Penerima</TableHead>
                  <TableHead>Keterangan</TableHead>
                  <TableHead className="text-right">Kerugian (HPP)</TableHead>
                  <TableHead>Dibuat Oleh</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingList && <TableRow><TableCell colSpan={8} className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></TableCell></TableRow>}
                {!loadingList && report.items.map((d) => (
                  <TableRow key={d.id} className="hover:bg-slate-50" data-testid={`special-row-${d.id}`}>
                    <TableCell className="font-mono text-xs">{d.number}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{dateTimeFmt(d.created_at)}</TableCell>
                    <TableCell>{d.branch_name}</TableCell>
                    <TableCell className="font-medium">{d.recipient_name}</TableCell>
                    <TableCell className="max-w-[220px] truncate text-sm text-muted-foreground">{d.note}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-rose-600">{rupiah(d.cogs_total)}</TableCell>
                    <TableCell className="text-xs">{d.created_by_name}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => setDetail(d)} data-testid={`special-detail-${d.id}`}>
                        <FileText className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!loadingList && !report.items.length && <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Belum ada penjualan khusus pada periode ini</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="special-detail-dialog">
          <DialogHeader>
            <DialogTitle>Detail {detail?.number}</DialogTitle>
            <DialogDescription>
              {detail?.branch_name} · Penerima: <b>{detail?.recipient_name}</b>
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              <p className="text-sm"><span className="text-muted-foreground">Keterangan: </span>{detail.note}</p>
              <div className="rounded-lg border">
                <Table>
                  <TableHeader><TableRow><TableHead>Produk</TableHead><TableHead>Qty</TableHead><TableHead className="text-right">HPP</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(detail.items || []).map((it, i) => (
                      <TableRow key={i}>
                        <TableCell>{it.name}</TableCell>
                        <TableCell>{it.qty} {it.unit}</TableCell>
                        <TableCell className="text-right font-mono">{rupiah(it.cogs)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-rose-50 px-3 py-2 text-sm">
                <span className="text-rose-700">Total Kerugian</span>
                <span className="font-bold text-rose-700">{rupiah(detail.cogs_total)}</span>
              </div>
              <Badge variant="outline">Dibuat oleh {detail.created_by_name}</Badge>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
